import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sun, Moon, Users, CheckCircle, Clock, DollarSign, LogOut, Copy, UserCircle, LayoutDashboard, ChevronDown, ChevronUp, Link } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import PartnerProfile from './PartnerProfile';

import { parseUtc } from '../lib/parseUtc';
const PARTNER_API = process.env.REACT_APP_PARTNER_BACKEND_URL || 'http://localhost:8081';

const partnerAxios = axios.create({ baseURL: PARTNER_API });

const PartnerDashboard = ({ setIsAuthenticated }) => {
  const { theme, toggleTheme } = useTheme();
  const [activePage, setActivePage] = useState('dashboard');
  const [stats, setStats] = useState({ total: 0, signed: 0, pending: 0, commission: 0, referral_code: '' });
  const [referrals, setReferrals] = useState([]);
  const [expandedUser, setExpandedUser] = useState(null);
  const [savingsData, setSavingsData] = useState(null);
  const [savingsLoading, setSavingsLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('partner_token');
    if (token) {
      partnerAxios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    fetchDashboardData();
    fetchReferrals();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await partnerAxios.get('/api/dashboard/stats');
      const d = response.data;
      setStats({
        total: d.total_referrals || 0,
        signed: d.signed_count || 0,
        pending: d.pending_count || 0,
        commission: d.total_commission || 0,
        referral_code: d.referral_code || '',
      });
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    }
  };

  const fetchReferrals = async () => {
    try {
      const response = await partnerAxios.get('/api/referrals');
      const data = response.data;
      setReferrals(data.referrals || data || []);
    } catch (error) {
      toast.error('Failed to fetch referrals');
    }
  };

  const handleUserClick = async (email) => {
    if (expandedUser === email) {
      setExpandedUser(null);
      return;
    }
    setExpandedUser(email);
    setSavingsLoading(true);
    try {
      const response = await partnerAxios.get('/api/referrals/savings');
      const user = (response.data.users || []).find(u => u.email === email);
      setSavingsData(user || null);
    } catch {
      setSavingsData(null);
      toast.error('Failed to fetch savings data');
    } finally {
      setSavingsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('partner_token');
    delete partnerAxios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
  };

  const copyReferralCode = () => {
    const code = stats.referral_code;
    if (!code) {
      toast.error('No referral code available');
      return;
    }
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(() => toast.success('Referral code copied!'));
    } else {
      const ta = document.createElement('textarea');
      ta.value = code;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success('Referral code copied!');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      signed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      churned: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.pending}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-background-light dark:bg-background-dark">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 min-h-screen bg-white dark:bg-surface-dark border-r dark:border-surface-border flex flex-col">
          <div className="p-6 flex flex-col items-center">
            <img
              src="/optops-logo.png"
              alt="OptOps.ai"
              className="h-16 w-auto dark:invert dark:hue-rotate-180"
            />
            <h1 className="text-xl font-bold mt-2">OptOps Partner</h1>
          </div>
          <nav className="px-4 space-y-2 flex-1">
            <button onClick={() => setActivePage('dashboard')} className={`flex items-center w-full px-3 py-2 rounded-md ${activePage === 'dashboard' ? 'text-primary bg-primary/10' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
              <LayoutDashboard className="h-5 w-5 mr-3" />
              Dashboard
            </button>
            <button onClick={() => setActivePage('profile')} className={`flex items-center w-full px-3 py-2 rounded-md ${activePage === 'profile' ? 'text-primary bg-primary/10' : 'hover:bg-gray-100 dark:hover:bg-gray-800'}`}>
              <UserCircle className="h-5 w-5 mr-3" />
              Profile
            </button>
          </nav>
          <div className="p-4">
            <Button variant="ghost" onClick={handleLogout} className="w-full justify-start">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Top Bar */}
          <div className="bg-white dark:bg-surface-dark border-b dark:border-surface-border px-6 py-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold">{activePage === 'dashboard' ? 'Partner Dashboard' : 'Partner Profile'}</h2>
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>
          </div>

          {activePage === 'profile' ? (
            <PartnerProfile />
          ) : (
          <div className="p-6 space-y-6">
            {/* Referral Code Card */}
            <Card className="border-primary/30 bg-primary/5 dark:bg-primary/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Your Referral Code</p>
                    <p className="text-3xl font-bold font-mono tracking-wider mt-1">{stats.referral_code}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                      Share this code with companies. When they sign up on OptOps using your code, they'll appear here.
                    </p>
                    {stats.referral_code && (
                      <div className="mt-3 space-y-2">
                        {[
                          `https://console.optops.ai/signup?referral=${stats.referral_code}`,
                          `http://localhost:3000/signup?referral=${stats.referral_code}`,
                        ].map((link) => (
                          <div key={link} className="flex items-center gap-2">
                            <Link className="h-4 w-4 text-gray-400 shrink-0" />
                            <a
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary underline break-all"
                            >
                              {link}
                            </a>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0"
                              onClick={() => {
                                navigator.clipboard.writeText(link).then(() => toast.success('Referral link copied!'));
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button onClick={copyReferralCode} variant="outline" className="gap-2">
                    <Copy className="h-4 w-4" />
                    Copy Code
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Stats Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Users className="h-8 w-8 text-primary" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Referrals</p>
                      <p className="text-2xl font-bold">{stats.total}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <CheckCircle className="h-8 w-8 text-success" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Signed</p>
                      <p className="text-2xl font-bold">{stats.signed}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <Clock className="h-8 w-8 text-yellow-500" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Pending</p>
                      <p className="text-2xl font-bold">{stats.pending}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <DollarSign className="h-8 w-8 text-success" />
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Commission</p>
                      <p className="text-2xl font-bold">${stats.commission}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Referred Users Table */}
            <Card>
              <CardHeader>
                <CardTitle>Referred Users</CardTitle>
              </CardHeader>
              <CardContent>
                {referrals.length === 0 ? (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No referrals yet. Share your code to get started!
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b dark:border-surface-border">
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Name</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Email</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Plan</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Commission %</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Commission</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Status</th>
                          <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600 dark:text-gray-400">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {referrals.map((r) => (
                          <React.Fragment key={r.id}>
                            <tr onClick={() => handleUserClick(r.user_email)} className="border-b dark:border-surface-border cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50">
                              <td className="py-3 px-4 font-medium">{r.user_name}</td>
                              <td className="py-3 px-4">{r.user_email}</td>
                              <td className="py-3 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  r.plan === 'enterprise' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                                  r.plan === 'professional' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                                  r.plan === 'starter' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                  'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                                }`}>{r.plan || 'free'}</span>
                              </td>
                              <td className="py-3 px-4 font-medium text-green-600">{r.commission_percentage != null ? `${r.commission_percentage}%` : '-'}</td>
                              <td className="py-3 px-4 font-medium text-green-600">${r.commission != null ? r.commission : '-'}</td>
                              <td className="py-3 px-4">{getStatusBadge(r.status)}</td>
                              <td className="py-3 px-4 flex items-center justify-between">
                                {parseUtc(r.created_at).toLocaleDateString()}
                                {expandedUser === r.user_email ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                              </td>
                            </tr>
                            {expandedUser === r.user_email && (
                              <tr className="border-b dark:border-surface-border bg-gray-50 dark:bg-gray-800/30">
                                <td colSpan={7} className="py-4 px-6">
                                  {savingsLoading ? (
                                    <p className="text-sm text-gray-500">Loading savings data...</p>
                                  ) : savingsData ? (
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Cost Before Optimization</p>
                                        <p className="font-semibold">${savingsData.cost_before_optimization?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Optimized Cost</p>
                                        <p className="font-semibold">${savingsData.optimized_cost?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Total Savings</p>
                                        <p className="font-semibold text-green-600">${savingsData.total_savings?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Savings %</p>
                                        <p className="font-semibold text-green-600">{savingsData.savings_percentage?.toFixed(1)}%</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Hourly Savings Rate</p>
                                        <p className="font-semibold">${savingsData.active_hourly_savings_rate?.toFixed(2)}/hr</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Clusters</p>
                                        <p className="font-semibold">{savingsData.clusters_count}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Baseline Monthly Cost</p>
                                        <p className="font-semibold">${savingsData.baseline_monthly_cost?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Current Monthly Cost</p>
                                        <p className="font-semibold">${savingsData.current_monthly_cost?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Projected Monthly Savings</p>
                                        <p className="font-semibold text-green-600">${savingsData.projected_monthly_savings?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Realized Monthly Savings</p>
                                        <p className="font-semibold text-green-600">${savingsData.realized_monthly_savings?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500 dark:text-gray-400">Active Savings</p>
                                        <p className="font-semibold text-green-600">${savingsData.active_savings?.toFixed(2)}</p>
                                      </div>
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500">No savings data available for this user.</p>
                                  )}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerDashboard;
